// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure rules behind the Agents page (docs/konvo-agents/DESIGN.md): reading the
// route, ordering threads (needs you, then running, then the newest), grouping them by project,
// the shared-projects summary on a machine and the short times in the list. No MobX, no I/O, so
// AgentsModel.test.ts covers it directly.

import type {KonvoMailDetail} from '@app/features/konvo_mail/state/KonvoMail';
import type {
	AgentLimits,
	AgentMachineView,
	AgentModel,
	AgentModelChoice,
	AgentProject,
	AgentQuestionRequest,
	AgentSnooze,
	AgentsSeen,
	AgentThreadDetail,
	AgentThreadItem,
	AgentThreadSource,
	AgentThreadSummary,
} from '@fluxer/schema/src/domains/konvo/Agents';
import {AGENT_PULL_REPOSITORY} from '@fluxer/schema/src/domains/konvo/Agents';

export interface AgentsLocation {
	machineId: string | null;
	threadId: string | null;
	/** /agents/new: the new chat page (its machine and project ride in the query). */
	newChat?: boolean;
	/** /agents/pulls: the Pull requests list; `pull` is the one open (/agents/pulls/:owner/:repo/:number). */
	pulls?: boolean;
	pull?: {repository: string; number: number} | null;
	/** /agents/runs: GitHub Actions runs; `run` is the one open (/agents/runs/:owner/:repo/:runId). */
	runs?: boolean;
	run?: {repository: string; runId: number} | null;
	/** /agents/machines/:machineId: how that machine is doing (machine IDs are never "machines"). */
	stats?: boolean;
}

/** History and live windows can split a question from its delivery. Prefer its rich answer. */
export function mergeThreadItems(
	earlier: ReadonlyArray<AgentThreadItem>,
	recent: ReadonlyArray<AgentThreadItem>,
): Array<AgentThreadItem> {
	const items = new Map<string, AgentThreadItem>();
	for (const item of [...earlier, ...recent]) {
		const existing = items.get(item.id);
		if (existing?.kind === 'user' && existing.answers?.length && !(item.kind === 'user' && item.answers?.length))
			continue;
		items.set(item.id, item);
	}
	return [...items.values()];
}

function decode(segment: string | undefined): string | null {
	if (!segment) return null;
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
}

/** /agents, /agents/new, /agents/:machineId or /agents/:machineId/:threadId (machine IDs are never "new"). */
export function parseAgentsPath(pathname: string): AgentsLocation {
	const parts = pathname.split('/').filter(Boolean);
	if (parts[0] !== 'agents') return {machineId: null, threadId: null};
	if (parts[1] === 'new' && parts.length === 2) return {machineId: null, threadId: null, newChat: true};
	if (parts[1] === 'machines' && parts.length === 3) return {machineId: decode(parts[2]), threadId: null, stats: true};
	if (parts[1] === 'pulls') {
		const owner = decode(parts[2]);
		const repo = decode(parts[3]);
		const number = Number(parts[4]);
		const repository = owner && repo ? `${owner}/${repo}` : '';
		const pull =
			parts.length === 5 && AGENT_PULL_REPOSITORY.test(repository) && Number.isInteger(number) && number > 0
				? {repository, number}
				: null;
		return {machineId: null, threadId: null, pulls: true, pull};
	}
	if (parts[1] === 'runs') {
		const owner = decode(parts[2]);
		const repo = decode(parts[3]);
		const runId = Number(parts[4]);
		const repository = owner && repo ? `${owner}/${repo}` : '';
		const run =
			parts.length === 5 && AGENT_PULL_REPOSITORY.test(repository) && Number.isSafeInteger(runId) && runId > 0
				? {repository, runId}
				: null;
		return {machineId: null, threadId: null, runs: true, run};
	}
	return {machineId: decode(parts[1]), threadId: decode(parts[2])};
}

type WorkFields = Pick<AgentThreadSummary, 'needsYou' | 'status'> & Partial<Pick<AgentThreadSummary, 'background'>>;

/**
 * The agent's turn ended but its subagents or background jobs are still at work (konvo-agents
 * 0.16.0+); they wake it again when they finish. Monitors alone don't count: they can watch for hours.
 */
export function isBackgroundWorking(thread: WorkFields): boolean {
	return (
		thread.background === 'working' && !thread.needsYou && thread.status !== 'running' && thread.status !== 'error'
	);
}

/** Running, or its subagents still are: the list shows both as working. */
export function isWorking(thread: WorkFields): boolean {
	return thread.status === 'running' || isBackgroundWorking(thread);
}

type RankFields = WorkFields & Partial<Pick<AgentThreadSummary, 'limit'>>;

/** Needs you first, then working (and paused on the plan limit: it goes on), then everything else. */
export function threadRank(thread: RankFields): number {
	if (thread.needsYou) return 0;
	if (isWorking(thread)) return 1;
	if (thread.status === 'error' && thread.limit?.kind === 'usage') return 1;
	return 2;
}

export function isActiveThread(thread: RankFields): boolean {
	return threadRank(thread) < 2;
}

/**
 * In the agent tool's Settled section: finished work put away, kept for search. Older bridges only
 * send settledAt. A thread that runs or needs you is never treated as settled, even before the
 * tool's own un-settle reaches Konvo.
 */
export function isSettled(
	thread: Pick<AgentThreadSummary, 'needsYou' | 'status' | 'settled' | 'settledAt'> &
		Partial<Pick<AgentThreadSummary, 'limit'>>,
): boolean {
	return !isActiveThread(thread) && (thread.settled ?? Boolean(thread.settledAt));
}

function time(iso: string | undefined | null): number {
	const at = iso ? Date.parse(iso) : Number.NaN;
	return Number.isFinite(at) ? at : 0;
}

/**
 * When the thread last started work (a prompt went in): the time the list orders by. A working
 * thread's updatedAt moves with every bit of output, so ordering by it reshuffled the list every few
 * seconds. Older bridges don't send turnStartedAt; their updatedAt stands in.
 */
export function activityAt(thread: Pick<AgentThreadSummary, 'turnStartedAt' | 'updatedAt'>): number {
	return time(thread.turnStartedAt) || time(thread.updatedAt);
}

/** The list order: needs you, running, then the most recently started. */
export function sortThreads<T extends AgentThreadSummary>(threads: ReadonlyArray<T>): Array<T> {
	return [...threads].sort(
		(a, b) => threadRank(a) - threadRank(b) || activityAt(b) - activityAt(a) || a.id.localeCompare(b.id),
	);
}

export interface ProjectGroup {
	id: string;
	title: string;
	threads: Array<AgentThreadSummary>;
	/** Running or needs-you threads in it. */
	active: number;
	latest: number;
}

/**
 * Threads grouped by project. Projects with something going on come first, then the one whose
 * work started most recently. A thread whose project the machine no longer lists keeps its project id as the title.
 */
export function groupByProject(
	threads: ReadonlyArray<AgentThreadSummary>,
	projects: ReadonlyArray<AgentProject>,
	query = '',
): Array<ProjectGroup> {
	const titles = new Map(projects.map((project) => [project.id, project.title]));
	const needle = query.trim().toLocaleLowerCase();
	const groups = new Map<string, ProjectGroup>();
	for (const thread of threads) {
		const title = titles.get(thread.projectId) ?? lastPathPart(thread.projectId);
		if (needle && !`${thread.title} ${title}`.toLocaleLowerCase().includes(needle)) continue;
		let group = groups.get(thread.projectId);
		if (!group) {
			group = {id: thread.projectId, title, threads: [], active: 0, latest: 0};
			groups.set(thread.projectId, group);
		}
		group.threads.push(thread);
		if (isActiveThread(thread)) group.active += 1;
		group.latest = Math.max(group.latest, activityAt(thread));
	}
	const list = [...groups.values()];
	for (const group of list) group.threads = sortThreads(group.threads);
	return list.sort(
		(a, b) => Number(b.active > 0) - Number(a.active > 0) || b.latest - a.latest || a.id.localeCompare(b.id),
	);
}

function lastPathPart(value: string): string {
	const parts = value.split(/[\\/]/).filter(Boolean);
	return parts.at(-1) ?? value;
}

/** What the list's status lens counts. Failures count as needing you: they wait on the person too. */
export type StatusLens = 'all' | 'needs' | 'running' | 'completed';

export function lensOf(thread: WorkFields): Exclude<StatusLens, 'all'> | null {
	if (thread.needsYou || thread.status === 'error') return 'needs';
	if (isWorking(thread)) return 'running';
	if (thread.status === 'completed' || thread.status === 'interrupted') return 'completed';
	return null;
}

export function lensCounts(threads: ReadonlyArray<WorkFields>): Record<StatusLens, number> {
	const counts: Record<StatusLens, number> = {all: threads.length, needs: 0, running: 0, completed: 0};
	for (const thread of threads) {
		const lens = lensOf(thread);
		if (lens) counts[lens] += 1;
	}
	return counts;
}

// ── Settled view ─────────────────────────────────────────────────────

/** When a settled thread's work ended (its turn, else its last update): what the Settled view sorts by. */
export function endedAt(thread: Pick<AgentThreadSummary, 'turnFinishedAt' | 'updatedAt'>): number {
	return time(thread.turnFinishedAt ?? thread.updatedAt);
}

/** When an archived thread was archived (else when it last changed). */
export function archivedAt(thread: Pick<AgentThreadSummary, 'archivedAt' | 'updatedAt'>): number {
	return time(thread.archivedAt ?? thread.updatedAt);
}

export type SettledDay = 'today' | 'yesterday' | 'week' | 'earlier';

export interface SettledDayGroup<T> {
	day: SettledDay;
	threads: Array<T>;
}

/**
 * Newest first, in Today / Yesterday / the last seven days / earlier, by local calendar day of `at`
 * (when the work ended; the Archived view passes when it was archived).
 */
export function settledDays<T extends AgentThreadSummary>(
	threads: ReadonlyArray<T>,
	now: number,
	at: (thread: T) => number = endedAt,
): Array<SettledDayGroup<T>> {
	const today = new Date(now);
	today.setHours(0, 0, 0, 0);
	const startToday = today.getTime();
	const startYesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1).getTime();
	const startWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6).getTime();
	const groups = new Map<SettledDay, Array<T>>();
	for (const thread of [...threads].sort((a, b) => at(b) - at(a) || a.id.localeCompare(b.id))) {
		const when = at(thread);
		const day: SettledDay =
			when >= startToday ? 'today' : when >= startYesterday ? 'yesterday' : when >= startWeek ? 'week' : 'earlier';
		groups.set(day, [...(groups.get(day) ?? []), thread]);
	}
	return (['today', 'yesterday', 'week', 'earlier'] as const).flatMap((day) =>
		groups.has(day) ? [{day, threads: groups.get(day)!}] : [],
	);
}

export interface ProjectCount {
	id: string;
	title: string;
	count: number;
}

/** Projects by how many of these threads they hold, most first (the Settled view's chips). */
export function projectCounts(
	threads: ReadonlyArray<Pick<AgentThreadSummary, 'projectId'>>,
	projects: ReadonlyArray<AgentProject>,
): Array<ProjectCount> {
	const titles = new Map(projects.map((project) => [project.id, project.title]));
	const counts = new Map<string, number>();
	for (const thread of threads) counts.set(thread.projectId, (counts.get(thread.projectId) ?? 0) + 1);
	return [...counts.entries()]
		.map(([id, count]) => ({id, title: titles.get(id) ?? lastPathPart(id), count}))
		.sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}

export function projectTitle(projectId: string, projects: ReadonlyArray<AgentProject>): string {
	return projects.find((project) => project.id === projectId)?.title ?? lastPathPart(projectId);
}

export function countThreads(threads: ReadonlyArray<WorkFields>): {
	running: number;
	needsYou: number;
} {
	let running = 0;
	let needsYou = 0;
	for (const thread of threads) {
		if (thread.needsYou) needsYou += 1;
		else if (isWorking(thread)) running += 1;
	}
	return {running, needsYou};
}

// ── Status marks (design/konvo-agents/r3-status.png) ────────────────────

/**
 * The mark a thread shows: a spinner while it (or its subagents) runs; an ink "?" when it needs you; red "!" when it
 * failed; a green double tick once it finished until the person opens it, then grey ticks like a
 * read receipt. A stopped thread reads as finished. Nothing for a thread that never ran.
 */
export type ThreadMark = 'running' | 'needs' | 'paused' | 'error' | 'done' | 'seen' | 'none';

type MarkFields = Pick<AgentThreadSummary, 'needsYou' | 'status' | 'updatedAt' | 'turnFinishedAt'> &
	Partial<Pick<AgentThreadSummary, 'limit' | 'background'>>;

/**
 * When the thread's latest turn finished (older bridges: its last update). Not yet while its
 * subagents still work: it isn't finished for the person until they come back and it ends again.
 */
export function finishedAt(thread: MarkFields): string | null {
	if (thread.status !== 'completed' && thread.status !== 'interrupted' && thread.status !== 'error') return null;
	if (isBackgroundWorking(thread)) return null;
	return thread.turnFinishedAt ?? thread.updatedAt;
}

export const seenKey = (machineId: string, threadId: string) => `${machineId}/${threadId}`;

/** Finished after the person's marks began and after they last opened it, or marked unread. */
export function isUnseen(seen: AgentsSeen | null, machineId: string, thread: MarkFields & {id: string}): boolean {
	if (!seen || thread.needsYou || thread.status === 'error') return false;
	const at = finishedAt(thread);
	if (!at) return false;
	if (seen.unread?.includes(seenKey(machineId, thread.id))) return true;
	const finished = time(at);
	return finished > time(seen.since) && finished > time(seen.threads[seenKey(machineId, thread.id)]);
}

export function threadMark(seen: AgentsSeen | null, machineId: string, thread: MarkFields & {id: string}): ThreadMark {
	if (thread.needsYou) return 'needs';
	if (isWorking(thread)) return 'running';
	// Stopped because the plan's usage limit ran out: it goes on once the limit resets.
	if (thread.status === 'error') return thread.limit?.kind === 'usage' ? 'paused' : 'error';
	if (!finishedAt(thread)) return 'none';
	return isUnseen(seen, machineId, thread) ? 'done' : 'seen';
}

/** A collapsed project shows its most urgent mark: needs you, then error, paused, then finished not opened. */
export function urgentMark(marks: ReadonlyArray<ThreadMark>): ThreadMark | null {
	for (const mark of ['needs', 'error', 'paused', 'done'] as const) if (marks.includes(mark)) return mark;
	return null;
}

/** How long a turn ran, in whole seconds; null when the times don't make sense. */
export function durationSeconds(fromIso: string | undefined, to: string | undefined | number): number | null {
	const from = time(fromIso);
	const end = typeof to === 'number' ? to : time(to);
	if (!from || !end || end < from) return null;
	return Math.round((end - from) / 1000);
}

export type SharedProjects = {kind: 'all'} | {kind: 'some'; names: Array<string>; more: number} | {kind: 'none'};

/** "All projects", or the first few shared names and how many more. */
export function sharedProjects(limits: AgentLimits, projects: ReadonlyArray<AgentProject>, max = 5): SharedProjects {
	if (limits.projects === 'all') return {kind: 'all'};
	const titles = new Map(projects.map((project) => [project.id, project.title]));
	const names = limits.projects.map((id) => titles.get(id) ?? lastPathPart(id));
	if (names.length === 0) return {kind: 'none'};
	return {kind: 'some', names: names.slice(0, max), more: Math.max(0, names.length - max)};
}

/** What Konvo may do on the machine, from its limits. */
export function permissionLevel(limits: AgentLimits): 'read' | 'send' | 'approve' {
	if (limits.send && limits.approve) return 'approve';
	if (limits.send) return 'send';
	return 'read';
}

export function canSend(machine: Pick<AgentMachineView, 'online' | 'limits'> | null | undefined): boolean {
	return Boolean(machine?.online && machine.limits.send);
}

export function canApprove(machine: Pick<AgentMachineView, 'online' | 'limits'> | null | undefined): boolean {
	return Boolean(machine?.online && machine.limits.approve);
}

/** A new chat the person started that has no thread yet: its id in the thread's place in the URL. */
export const PENDING_THREAD_PREFIX = 'konvo-pending-';
export const isPendingThreadId = (id: string | null | undefined): id is string =>
	typeof id === 'string' && id.startsWith(PENDING_THREAD_PREFIX);

/** The machine's bridge can close an agent's question without an answer (konvo-agents 0.36.0+). */
export function canDismissQuestion(
	machine: Pick<AgentMachineView, 'online' | 'limits' | 'tools'> | null | undefined,
): boolean {
	return canSend(machine) && machine?.tools[0]?.can.dismissQuestion === true;
}

/** The machine's bridge takes secrets the agent asks for (konvo-agents 0.53.0+, T3 protocol 2). */
export function canAnswerSecret(
	machine: Pick<AgentMachineView, 'online' | 'limits' | 'tools'> | null | undefined,
): boolean {
	return canSend(machine) && machine?.tools[0]?.can.secretRequests === true;
}

/**
 * A thread without the questions the person dismissed. A list row (no questions on it) that waited
 * on a question no longer needs you; a thread's own view does once none of its questions are left.
 */
export function withoutQuestions<T extends AgentThreadSummary & {questions?: Array<AgentQuestionRequest>}>(
	thread: T,
	ids: ReadonlySet<string>,
): T {
	const questions = thread.questions;
	const left = questions?.filter((request) => !ids.has(request.requestId));
	const next = left && questions && left.length !== questions.length ? {...thread, questions: left} : thread;
	if (next.waitingOn !== 'question' || (left && left.length > 0)) return next;
	const {waitingOn: _waitingOn, ...rest} = next;
	return {...rest, needsYou: false} as T;
}

/** Laptops and desktops get a laptop icon, the rest a server. */
export function machineIcon(platform: AgentMachineView['platform']): 'laptop' | 'server' {
	return platform === 'darwin' || platform === 'win32' ? 'laptop' : 'server';
}

export function platformName(platform: AgentMachineView['platform']): string {
	switch (platform) {
		case 'darwin':
			return 'macOS';
		case 'linux':
			return 'Linux';
		case 'win32':
			return 'Windows';
		default:
			return '';
	}
}

/** Which brand mark a thread's provider gets. */
export function providerKind(provider: string | undefined): 'claude' | 'codex' | 't3' {
	const value = (provider ?? '').toLowerCase();
	if (value.includes('claude') || value.includes('anthropic')) return 'claude';
	if (value.includes('codex') || value.includes('openai') || value.includes('gpt')) return 'codex';
	return 't3';
}

export type ShortAgo =
	| {unit: 'now'}
	| {unit: 'minutes'; n: number}
	| {unit: 'hours'; n: number}
	| {unit: 'weekday'; at: number}
	| {unit: 'date'; at: number};

/** The short time in the list: now, 4m, 7h, Sat, 12 Sep. */
export function shortAgo(iso: string | undefined | null, now: number): ShortAgo {
	const at = time(iso);
	const diff = Math.max(0, now - at);
	if (diff < 60_000) return {unit: 'now'};
	if (diff < 60 * 60_000) return {unit: 'minutes', n: Math.floor(diff / 60_000)};
	if (diff < 24 * 60 * 60_000) return {unit: 'hours', n: Math.floor(diff / (60 * 60_000))};
	if (diff < 6 * 24 * 60 * 60_000) return {unit: 'weekday', at};
	return {unit: 'date', at};
}

/** Whole seconds left until `expiresAt` (never negative). */
export function secondsLeft(expiresAt: string, now: number): number {
	return Math.max(0, Math.ceil((time(expiresAt) - now) / 1000));
}

/** 9:41 */
export function formatCountdown(seconds: number): string {
	const minutes = Math.floor(seconds / 60);
	return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The API's error code from a failed request ({code} in the body), if any. */
export function agentErrorCode(error: unknown): string | null {
	if (!error || typeof error !== 'object') return null;
	const body = (error as {body?: unknown}).body;
	if (body && typeof body === 'object' && typeof (body as {code?: unknown}).code === 'string') {
		return (body as {code: string}).code;
	}
	const status = (error as {status?: unknown}).status;
	if (status === 403) return 'AGENTS_NOT_ENABLED';
	if (status === 404) return 'NOT_FOUND';
	return null;
}

/**
 * Konvo is mid-release or out of reach: Caddy answers 502/503/504 while the API is replaced, or
 * the request never left. Worth waiting out; anything else (MACHINE_OFFLINE included) is an answer.
 */
export function isReleaseGap(error: unknown): boolean {
	if (!error || typeof error !== 'object') return false;
	const status = (error as {status?: unknown}).status;
	if (typeof status === 'number') return status === 502 || status === 503 || status === 504;
	return error instanceof Error && error.message === 'Network error during request';
}

/**
 * Keep the loaded detail when an update carries the same revision and the same state. The revision
 * only follows the thread's timeline; status, needs you and the rest come from the tool's thread
 * list, which can catch up after the last timeline event (a run that ends right after its reply).
 */
export function shouldReplaceDetail(previous: AgentThreadDetail | null, next: AgentThreadDetail): boolean {
	if (!previous) return true;
	if (previous.id !== next.id) return true;
	if (previous.revision !== next.revision) return true;
	const {items: _before, ...before} = previous;
	const {items: _after, ...after} = next;
	return JSON.stringify(before) !== JSON.stringify(after);
}

/** A summary that says the open thread moved on (status, needs you or a newer update). */
export function summaryDiffers(detail: AgentThreadDetail, summary: AgentThreadSummary): boolean {
	return (
		detail.status !== summary.status ||
		detail.needsYou !== summary.needsYou ||
		time(summary.updatedAt) > time(detail.updatedAt)
	);
}

/** The machines that came back online since the last overview. */
export function cameOnline(
	before: ReadonlyArray<Pick<AgentMachineView, 'id' | 'online'>>,
	after: ReadonlyArray<Pick<AgentMachineView, 'id' | 'online'>>,
): Array<string> {
	const wasOnline = new Map(before.map((machine) => [machine.id, machine.online]));
	return after.filter((machine) => machine.online && wasOnline.get(machine.id) === false).map((machine) => machine.id);
}

/** The models Konvo can pick on a machine (konvo-agents 0.9.0+), or none on older bridges. */
export function pickableModels(machine: Pick<AgentMachineView, 'tools'> | null | undefined): Array<AgentModel> {
	const tool = machine?.tools[0];
	return tool?.can.model && tool.models?.length ? tool.models : [];
}

/**
 * The model, reasoning level and service tier a thread runs on. Bridges before 0.13.0 report only
 * the model's id; that one is used when it names exactly one model the machine lists.
 */
export function threadModelChoice(
	machine: Pick<AgentMachineView, 'tools'> | null | undefined,
	thread: Pick<AgentThreadSummary, 'model' | 'modelChoice'> | null | undefined,
): AgentModelChoice | null {
	if (thread?.modelChoice) return thread.modelChoice;
	if (!thread?.model) return null;
	const name = thread.model.toLowerCase();
	const matches = pickableModels(machine).filter((model) =>
		[model.model, model.name, ...(model.aliases ?? [])].some((value) => value.toLowerCase() === name),
	);
	return matches.length === 1 ? {provider: matches[0]!.provider, model: matches[0]!.model} : null;
}

/** The model a choice names on this machine. */
export function modelFor(
	machine: Pick<AgentMachineView, 'tools'> | null | undefined,
	choice: AgentModelChoice | null | undefined,
): AgentModel | null {
	if (!choice) return null;
	return (
		pickableModels(machine).find((model) => model.provider === choice.provider && model.model === choice.model) ?? null
	);
}

/** What a thread runs on, as its list row and hover card name it. */
export interface ThreadModel {
	choice: AgentModelChoice | null;
	/** The model as the machine lists it, when it does. */
	model: AgentModel | null;
	/** Short, without the brand the logo already shows: "Opus 5.5". */
	name: string;
	/** The provider instance, for the logo and the login. */
	provider: string;
}

/** The model a thread runs on: the one the machine lists, else the id the thread reports. */
export function threadModel(
	machine: Pick<AgentMachineView, 'tools'> | null | undefined,
	thread: Pick<AgentThreadSummary, 'provider' | 'model' | 'modelChoice'>,
): ThreadModel | null {
	const choice = threadModelChoice(machine, thread);
	const model = modelFor(machine, choice);
	const name = model?.name ?? choice?.model ?? thread.model;
	if (!name) return null;
	return {choice, model, name: shortModelName(name), provider: choice?.provider ?? thread.provider};
}

/** "Opus 5.5" from "Claude Opus 5.5"; other names stay as they are. */
export function shortModelName(name: string): string {
	return name.replace(/^claude\s+(?=\S)/i, '').trim() || name;
}

/** What a new thread in this project runs on when no model is picked: the project's, then the machine's. */
export function defaultModelOf(
	machine: Pick<AgentMachineView, 'tools' | 'projects'> | null | undefined,
	projectId: string | null | undefined,
): AgentModelChoice | null {
	const project = machine?.projects.find((entry) => entry.id === projectId);
	return project?.model ?? machine?.tools[0]?.defaultModel ?? null;
}

/**
 * The replies that close a turn: in each run of items between two prompts, the last reply, and in
 * the last run only once the agent has stopped. They show as cards; the replies before them are
 * working updates and show flat, like T3 Code's.
 */
export function turnSummaries(
	items: ReadonlyArray<Pick<AgentThreadItem, 'kind' | 'id'>>,
	running: boolean,
): Set<string> {
	const summaries = new Set<string>();
	let last: string | null = null;
	for (const item of items) {
		if (item.kind === 'user') {
			if (last) summaries.add(last);
			last = null;
		} else if (item.kind === 'assistant') last = item.id;
	}
	if (last && !running) summaries.add(last);
	return summaries;
}

/** What a thread shows: its items, with each finished turn's steps folded into one block. */
export type ThreadBlock<T> = {kind: 'item'; item: T} | {kind: 'steps'; id: string; items: Array<T>};

/**
 * A turn that has its summary card shows only the prompt and the card; the updates and tool steps
 * that led to it fold into one "steps" block placed before the card, opened on demand. Its
 * subagents stay visible (a card of their own, closed) between the steps and the summary. A turn
 * that is still running shows everything as it happens.
 */
export function threadBlocks<T extends Pick<AgentThreadItem, 'kind' | 'id'>>(
	items: ReadonlyArray<T>,
	summaries: ReadonlySet<string>,
): Array<ThreadBlock<T>> {
	const blocks: Array<ThreadBlock<T>> = [];
	let turn: Array<T> = [];
	const flush = () => {
		const summary = turn.find((item) => summaries.has(item.id));
		if (summary) {
			// Notes (a compaction) stay in view, where they happened: before the steps or after the card.
			const at = turn.indexOf(summary);
			for (const item of turn.slice(0, at)) if (item.kind === 'note') blocks.push({kind: 'item', item});
			const steps = turn.filter((item) => item !== summary && item.kind !== 'subagents' && item.kind !== 'note');
			if (steps.length) blocks.push({kind: 'steps', id: `steps-${summary.id}`, items: steps});
			for (const item of turn) if (item.kind === 'subagents') blocks.push({kind: 'item', item});
			blocks.push({kind: 'item', item: summary});
			for (const item of turn.slice(at + 1)) if (item.kind === 'note') blocks.push({kind: 'item', item});
		} else {
			for (const item of turn) blocks.push({kind: 'item', item});
		}
		turn = [];
	};
	for (const item of items) {
		if (item.kind === 'user') {
			flush();
			blocks.push({kind: 'item', item});
		} else turn.push(item);
	}
	flush();
	return blocks;
}

// ── Send to an agent ─────────────────────────────────────────────────

/** Something in Konvo a new agent chat starts from: a message, an email or a ClickUp task. */
export interface AgentChatContext {
	/** 'agent': another agent chat this one carries on from (it stopped on its plan). */
	kind: 'message' | 'whatsapp' | 'mail' | 'task' | 'agent';
	/** Who and where, e.g. "Markus Mulder in #konvo-dev" or "ClickUp task: Fix Lieben totals". */
	label: string;
	text: string;
	/** Names to guess the project from: the channel, the ClickUp list and space, the sender's domain. */
	hints: Array<string>;
	/** The person and message to reply to once the agent is done; for a task, the task its time belongs to. */
	source?: AgentThreadSource;
	/** An email's or chat message's files and pictures, on their way: shown with the quote and sent with it. */
	files?: Promise<ReadonlyArray<File>>;
	/** Their names, known at once: the prompt names them, so the agent knows of one that could not come along. */
	fileNames?: ReadonlyArray<string>;
	/** An email as sent: the Send to an agent panel draws it like the mail reader; the prompt still gets `text`. */
	mail?: KonvoMailDetail;
}

/** The longest context the prompt carries; the schema allows 20 000 characters in all. */
export const CONTEXT_MAX_CHARS = 8000;

const words = (value: string) =>
	` ${value
		.toLocaleLowerCase()
		.replace(/[^\p{L}\p{N}]+/gu, ' ')
		.trim()} `;

/**
 * The project a hint names, as whole words (#konvo-dev names konvo; "konvoy" does not). The longest
 * matching title wins, so icam-vizion-reports beats icam; a project's folder name counts as well.
 */
export function guessProject(
	projects: ReadonlyArray<Pick<AgentProject, 'id' | 'title' | 'path'>>,
	hints: ReadonlyArray<string>,
): {projectId: string; hint: string} | null {
	let best: {projectId: string; hint: string; length: number} | null = null;
	for (const project of projects) {
		const names = [project.title, project.path?.split('/').filter(Boolean).at(-1) ?? '']
			.map((name) => words(name).trim())
			.filter((name) => name.length >= 2);
		for (const hint of hints) {
			const haystack = words(hint);
			for (const name of names) {
				if (haystack.includes(` ${name} `) && (!best || name.length > best.length))
					best = {projectId: project.id, hint, length: name.length};
			}
		}
	}
	return best ? {projectId: best.projectId, hint: best.hint} : null;
}

/** The prompt as the agent gets it: the person's words, then the item from Konvo, quoted. */
/** `filesLine`: the item's files named ("Attachments: …"), after the text so a long one cut short keeps it. */
export function promptWithContext(prompt: string, context: AgentChatContext | null, filesLine?: string | null): string {
	const text = context?.text.trim() ?? '';
	if (!context || (!text && !filesLine)) return prompt;
	const clipped = text.length > CONTEXT_MAX_CHARS ? `${text.slice(0, CONTEXT_MAX_CHARS - 1)}…` : text;
	const quoted = [clipped, filesLine ?? '']
		.filter(Boolean)
		.join('\n')
		.split('\n')
		.map((line) => (line ? `> ${line}` : '>'))
		.join('\n');
	return `${prompt}\n\nContext from Konvo (${context.label}):\n${quoted}`;
}

/**
 * Whether a snooze is over: its time passed, or, snoozed until it needs you, the chat asked
 * something, stopped on an error or finished a turn since it was snoozed.
 */
export function snoozeEnded(
	snooze: Pick<AgentSnooze, 'until' | 'untilNeedsYou' | 'at'>,
	thread: WorkFields & Partial<Pick<AgentThreadSummary, 'turnFinishedAt'>>,
	now: number,
): boolean {
	if (snooze.until) return Date.parse(snooze.until) <= now;
	if (!snooze.untilNeedsYou) return true;
	if (thread.needsYou || thread.status === 'error') return true;
	const finished = Date.parse(thread.turnFinishedAt ?? '') || 0;
	return thread.status !== 'running' && !isBackgroundWorking(thread) && finished > (Date.parse(snooze.at) || 0);
}
